import React from 'react'
import PropTypes from 'prop-types'

// constants
import { fallbackMessages } from 'app/static/frontend/widgets/constants/'

// components
import { Widget, TrendsWidgetContent } from 'quorumdesign'

// helpers
import { parseHtmlToReact } from 'app/static/frontend/imports/desktopHelperFunctions'
import { selectShowInSearchPermanentFilters } from 'widgets/selectors/listWidgetSelectors'

// style
import * as S from 'app/static/frontend/widgets/styles/trendsWidget'


/**
 * Renders a widget that displays a list of trending terms and their count
 * https://projects.invisionapp.com/share/7910N8YUT6A8
 *
 * Note: ideally these functions would all be shared between widgets that require
 * data fetching. However, because they are all sligthly different, it doesnt makse
 * sense to do so at this moment.
 */
export class TrendsWidget extends React.PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            data: undefined,
            isLoading: false,
            hasError: false
        }
        this.mounted = false
        this.segueWithKey = this.segueWithKey.bind(this)
    }

    componentDidMount() {
        this.mounted = true
        if (this.props.canLoad) {
            this.loadWidget()
        }
    }

    componentWillUnmount() {
        this.mounted = false
    }

    /**
     * Handles the sorting and setting of data and other states
     *
     * @param {Array<[String, Number]>} data - An array of the [terms, # of mentions] (may change)
     */
    setData(data) {
        const sortedAndFilteredData = (
            (data.length)
            ? (
                data
                .filter(term => term[1])  // some terms will be marked as significant but have doc count of O
                .sort((a, b) => b[1] - a[1])  // sorts greatest -> lowest # of mentions
            )
            : []
        )
        this.setState({
            data: sortedAndFilteredData,
            isLoading: false,
            hasError: false
        })
    }

    /**
     * Loads widget data for trends, if there is cachedData
     * use that, otherwise, evaluate using the current widget
     */
    async loadWidget() {
        if (this.props.cachedData) {
            this.setData(this.props.cachedData)
        } else {
            this.setState({ isLoading: true })
            try {
                const response = await this.props.evaluateWidget(this.props.widget)
                this.mounted && this.setData(response.data)
            } catch(e) {
                this.setState({
                    hasError: true,
                    isLoading: false
                })
            }
        }

    }

    /**
     * Segues to a search window creating a search using the user's
     * current safed_search with the advanced_search filter being the
     * term clicked on
     *
     * @param {String} key - the value of the term the user clicked on
     */
    segueWithKey(key) {
        if (this.props.canLoad && !this.state.isLoading && this.state.data && !this.props.editForm) {
            const safedSearch = (
                this.props.widget.data_sources.length &&
                this.props.widget.data_sources[0].safed_search
            )
            if (key.includes(' ')) {
                key = key.split(' ').join(' AND ')
            }

            const additional_filters = this.props.widget.data_sources.length ? this.props.widget.data_sources[0].additional_filters : {}

            const filters = [{
                ...additional_filters,
                advanced_search: key,
                estimated_published_date_range: 5
            }]

            // takes in the safed_seach and filter array and returns:
            // { dataType, filters: { filters: { advanced_search: key, estimated_published_date_range: 5 }}}
            const permFilters = selectShowInSearchPermanentFilters({ safedSearch, filters })

            this.props.showInSearch({...permFilters, dataType: DjangIO.app.models.QuorumDataType.media.value })
        }
    }


    /**
     * Handles various fallback states - loading | empty | error handling
     */
     renderFallback() {
        const { data, isLoading, hasError } = this.state

        if (this.props.canLoad && isLoading) {
            return fallbackMessages.LOADING
        } else if (hasError) {
            return fallbackMessages.DATA_ERROR
        } else if (!data) {
            return fallbackMessages.UNSELECTED
        } else if (!data.length) {
            return fallbackMessages.NO_DATA
        } else {
            return fallbackMessages.DEFAULT_ERROR
        }
    }

    render() {
        const {
            description,
            editing,
            editForm,
            headerIcons,
            headerTitle,
            widgetKey,
        } = this.props
        const { isLoading, data, hasError } = this.state

        return (
            <Widget
                description={description && parseHtmlToReact(description, widgetKey, false)}
                editing={editing}
                emptyPlaceholder={"Your Selection of Filter(s) Returned No Data"}
                headerIcons={!editForm ? headerIcons : [{iconFamily: "fas", icon: "chart-line", size: "lg"}]}
                headerTitle={headerTitle}
                id={widgetKey && `widget-${widgetKey}`}
                noHeader={!headerTitle}
                widget={(data && data.length) ? <TrendsWidgetContent data={data} handleSegue={this.segueWithKey}/> : ""}
                widgetContentIsLoading={isLoading}
                widgetFallback={( !data || !data.length || hasError || isLoading) && this.renderFallback()}
            />
        )
    }
}

export default TrendsWidget

TrendsWidget.propTypes = {
    cachedData: PropTypes.object,
    canLoad: PropTypes.bool,
    description: PropTypes.string,
    editing: PropTypes.bool,
    editForm: PropTypes.bool,
    headerIcons:  PropTypes.arrayOf(PropTypes.shape({
        iconFamily: PropTypes.string,
        icon: PropTypes.string,
        size: PropTypes.string,
      })),
    headerTitle: PropTypes.string,
    widgetKey: PropTypes.string,
}
